'use client';

import type { StripePromoCoupon, StripeStoreProduct } from '@inkitt/contracts-common';
import type { CountryCode } from '@inkitt/geo';
import { getFinalPriceAmountAfterCoupon, localizedPriceAmountWithDecimal } from '@inkitt/utils-stripe';
import { Check, X } from 'lucide-react';
import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useClientLocale } from '../../../../../../hooks/useClientLocale';
import { trackClientInfo } from '../../../../../../services/tracking/trackClientInfo';
import { partitionByInterval } from '../utils/bundle-utils';

type SubscriptionContentProps = {
  subscriptions: StripeStoreProduct[];
  countryCode: CountryCode;
  storyId?: string;
  episodeNumber?: string;
  coupon?: string;
  appliedCoupon?: StripePromoCoupon;
  onClearCoupon: () => void;
  onOpenPromoModal: () => void;
};

export const SubscriptionContent: React.FC<SubscriptionContentProps> = ({
  subscriptions,
  countryCode,
  storyId,
  episodeNumber,
  coupon,
  appliedCoupon,
  onClearCoupon,
  onOpenPromoModal,
}) => {
  const { t } = useTranslation();
  const { languageCode } = useClientLocale();
  const { monthly, yearly } = partitionByInterval(subscriptions);
  const [selectedBundle, setSelectedBundle] = useState<StripeStoreProduct | null>(yearly[0] || monthly[0] || null);
  const [screenHeight, setScreenHeight] = useState<number>(600);

  useEffect(() => {
    setScreenHeight(window.innerHeight);
  }, []);

  const handleBundleSelect = (bundle: StripeStoreProduct) => {
    setSelectedBundle(bundle);
    trackClientInfo('paywall-bundle-clicked', {
      priceId: bundle.price.id,
      productId: bundle.product.id,
      sku: bundle.product.metadata.sku,
      type: 'subscription',
    });
  };

  const handlePurchase = () => {
    if (!selectedBundle) {
      return;
    }

    const params = new URLSearchParams();
    params.set('price_id', selectedBundle.price.id);

    if (coupon) {
      params.set('coupon', coupon);
    }

    if (storyId) {
      params.set('storyId', storyId);
    }

    if (episodeNumber) {
      params.set('episodeNumber', episodeNumber);
    }

    const sku = selectedBundle.product.metadata.sku;
    window.location.href = `/u/store/sku/${sku}?${params.toString()}`;
  };

  const formatPrice = (bundle: StripeStoreProduct) => {
    const amount = bundle.price.unit_amount ?? 0;
    const finalAmount = getFinalPriceAmountAfterCoupon({
      amount,
      coupon: bundle.coupon,
      currency: bundle.price.currency,
    });

    return {
      current: localizedPriceAmountWithDecimal({
        amountInCents: finalAmount,
        currency: bundle.price.currency,
        countryCode,
        languageCode,
      }),
      original:
        finalAmount < amount
          ? localizedPriceAmountWithDecimal({
              amountInCents: amount,
              currency: bundle.price.currency,
              countryCode,
              languageCode,
            })
          : null,
    };
  };

  const formatMonthlyPrice = (bundle: StripeStoreProduct) => {
    const amount = bundle.price.unit_amount ?? 0;
    const finalAmount = getFinalPriceAmountAfterCoupon({
      amount,
      coupon: bundle.coupon,
      currency: bundle.price.currency,
    });

    const monthlyAmount = bundle.price.recurring?.interval === 'year' ? Math.round(finalAmount / 12) : finalAmount;

    return localizedPriceAmountWithDecimal({
      amountInCents: monthlyAmount,
      currency: bundle.price.currency,
      countryCode,
      languageCode,
    });
  };

  if (subscriptions.length === 0) {
    return (
      <div className="text-center py-8">
        <p className="text-neutral-400">{t('subscription:no_subscriptions_available', 'No subscriptions available')}</p>
      </div>
    );
  }

  const yearlyBundle = yearly[0];
  const monthlyBundle = monthly[0];

  const maxImageHeight = screenHeight / (screenHeight < 600 ? 5 : screenHeight < 750 ? 4 : 3.5);

  return (
    <div className="flex flex-col -mt-6">
      <div className="relative">
        <div className="absolute inset-0 bg-[linear-gradient(180deg,var(--color-neutral-900)_3%,transparent_5%,var(--color-neutral-900)_80%)]" />
        {/* Hero Image */}
        <img
          alt=""
          src="/images/background/paywall-bg.png"
          className="w-full object-cover"
          style={{ maxHeight: `${maxImageHeight}px` }}
        />

        {/* Header */}
        <h2 className="text-[28px] font-semibold text-white text-center leading-[30px] absolute bottom-0 z-0 px-4">
          {t('subscription:subscribe_benefits_title', 'Subscribe to the joy of uninterrupted reading!')}
        </h2>
      </div>

      {/* Benefits List */}
      <div className="space-y-3 mb-6 mt-6">
        {[
          t('subscription:benefit_unlimited', 'Unlimited access to all our stories and audiobooks'),
          t('checkout:benefits_no_wait_times', 'No wait times'),
          t('checkout:benefits_offline_reading', 'Offline access at no extra cost'),
          t('subscription:benefit_cancel', 'Cancel anytime'),
          t('subscription:benefit_new_content', '30 new seasons & 1000 new chapters every month'),
        ].map((benefit, index) => (
          <div key={index} className="flex items-start gap-2">
            <Check className="w-4 h-4 text-primary-300 flex-shrink-0 mt-1" />
            <span className="text-white text-sm font-bold leading-[26px]">{benefit}</span>
          </div>
        ))}
      </div>

      {/* Subscription Options */}
      <div className="space-y-2.5 mb-2">
        {yearlyBundle && (
          <div onClick={() => handleBundleSelect(yearlyBundle)} className="relative" role="button" tabIndex={0}>
            {/* Most Popular Badge */}
            <div className="absolute -top-1.5 left-4 z-10">
              <div className="bg-lime-300 text-black text-center rounded text-[9px] font-bold uppercase h-[15px] w-[100px]">
                {t('subscription:most_popular', 'Most Popular')}
              </div>
            </div>

            <div
              className={`relative bg-neutral-700 rounded-xl px-4 cursor-pointer transition-all border h-[70px] w-full flex items-center justify-between ${
                selectedBundle?.price.id === yearlyBundle.price.id ? 'border-primary-300' : 'border-neutral-100'
              }`}
            >
              <div className="flex items-center gap-4 w-full">
                {/* Radio Button */}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {selectedBundle?.price.id === yearlyBundle.price.id ? (
                    <div className="w-5 h-5 rounded-full bg-primary-300 border-2 border-primary-300 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-neutral-100" />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-base capitalize">
                      {t('subscription:yearly', 'Yearly')}
                    </span>
                    <div className="text-right flex flex-col items-end">
                      <div className="flex items-center flex flex-col">
                        {formatPrice(yearlyBundle).original && (
                          <span className="text-neutral-500 line-through text-xs">
                            {formatPrice(yearlyBundle).original}
                          </span>
                        )}
                        <span className="font-bold text-white text-[15px]">{formatPrice(yearlyBundle).current}</span>
                      </div>
                      <div className="text-xs text-neutral-300 mt-0.5">
                        {t('checkout:price_per_month', {
                          pricePerMonth: formatMonthlyPrice(yearlyBundle),
                        })}
                        {', '}
                        {t('subscription:billed_annually', 'billed annually')}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}

        {monthlyBundle && (
          <div onClick={() => handleBundleSelect(monthlyBundle)} className="relative" role="button" tabIndex={0}>
            <div
              className={`relative bg-neutral-700 rounded-xl px-4 cursor-pointer transition-all border h-[70px] w-full flex items-center justify-between ${
                selectedBundle?.price.id === monthlyBundle.price.id ? 'border-primary-300' : 'border-neutral-100'
              }`}
            >
              <div className="flex items-center gap-4 w-full">
                {/* Radio Button */}
                <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
                  {selectedBundle?.price.id === monthlyBundle.price.id ? (
                    <div className="w-5 h-5 rounded-full bg-primary-300 border-2 border-primary-300 flex items-center justify-center">
                      <Check className="w-5 h-5 text-white" strokeWidth={3} />
                    </div>
                  ) : (
                    <div className="w-5 h-5 rounded-full border-2 border-neutral-100" />
                  )}
                </div>

                <div className="flex-1">
                  <div className="flex items-center justify-between">
                    <span className="font-bold text-white text-base capitalize">
                      {t('subscription:monthly', 'Monthly')}
                    </span>
                    <div className="text-right flex flex-col items-end">
                      <div className="flex items-center flex flex-col">
                        {formatPrice(monthlyBundle).original && (
                          <span className="text-neutral-500 line-through text-xs">
                            {formatPrice(monthlyBundle).original}
                          </span>
                        )}
                        <span className="font-bold text-white text-[15px]">{formatPrice(monthlyBundle).current}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* CTA Button */}
      <button
        onClick={handlePurchase}
        disabled={!selectedBundle}
        className="w-full bg-primary-300 hover:bg-primary-200 text-white disabled:cursor-not-allowed text-white font-bold py-3.5 rounded-lg transition-colors text-base mt-2"
      >
        {t('paywall_content:get_unlimited_access', 'Get Unlimited Access')}
      </button>

      {/* Apply Promo Code Link - show when no coupon applied */}
      {!appliedCoupon && (
        <div className="mt-1 text-center">
          <button
            onClick={onOpenPromoModal}
            className="text-primary-300 hover:text-primary-200 font-bold text-sm underline"
          >
            {t('subscription:apply_promo_code', 'Apply promo code')}
          </button>
        </div>
      )}

      {/* Promo Code Banner - show when coupon applied */}
      {appliedCoupon && (
        <div className="mt-4 flex items-center justify-between">
          <span className="text-white text-sm text-left flex-1">
            {t('subscription:promo_applied_banner', `${appliedCoupon.percent_off}% Promotional Code Applied!`, {
              percent: appliedCoupon.percent_off,
            })}
          </span>
          <button
            onClick={onClearCoupon}
            className="p-1 hover:bg-black/20 rounded transition-colors"
            aria-label={String(t('subscription:clear_promo', 'Clear promo code'))}
          >
            <X className="w-3 h-3 text-white" />
          </button>
        </div>
      )}

      {/* Terms Links */}
      <div className="mt-6 flex items-center justify-center flex-wrap gap-x-4 gap-y-2 text-xs">
        <a href="https://galatea.com/terms" className="text-neutral-400 hover:text-neutral-500 underline">
          {t('shop:see_terms', 'See our Terms of Service')}
        </a>
        <a href="https://galatea.com/privacy" className="text-neutral-400 hover:text-neutral-500 underline">
          {t('shop:see_privacy', 'See our Privacy Policy')}
        </a>
      </div>
    </div>
  );
};
